import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  useAllEdges,
  useConcepts,
  useCourses,
  useExercises,
  useLessons,
  useModules,
  useSessions,
} from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { useSeeding } from '@/state/learnSeeding'
import { Button, Empty, PageTitle } from '@/components/ui'
import { activeCourse, deckDoneToday, KEEP_GOING_MINUTES, ONE_DECK_MINUTES } from '@/lib/learnDeck'
import { lessonProgress, outlineOf } from '@/lib/learn'
import { TestSetupSheet } from './TestSetupSheet'
import {
  AddConceptButton,
  AddCourseButton,
  AddTeachingButton,
  LearnTabs,
  useStartDeck,
  useTeachingGap,
} from './parts'

/**
 * Learn's home: today's deck, one big button, and the course in hand with its bar. Nothing here counts
 * what is due or what is left, and a course that has gone quiet is simply the one you pick up again.
 */
export const LearnHomeView = () => {
  const courses = useCourses() ?? []
  const modules = useModules() ?? []
  const lessons = useLessons() ?? []
  const concepts = useConcepts() ?? []
  const exercises = useExercises()
  const sessions = useSessions() ?? []
  const edges = useAllEdges()
  const now = useNow(60_000)
  const course = activeCourse(courses)
  const progress = course ? lessonProgress(outlineOf(course.id, { modules, lessons, concepts, edges })) : null
  const doneToday = deckDoneToday(sessions, new Date(now))
  const [minutes, setMinutes] = useState(ONE_DECK_MINUTES)
  const [none, setNone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const gap = useTeachingGap()
  const seeding = useSeeding((s) => s.on)
  const startDeck = useStartDeck()
  const navigate = useNavigate()
  const start = async () => {
    setBusy(true)
    try {
      if (await startDeck(minutes)) void navigate({ to: '/learn/deck' })
      else setNone(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div data-testid="learn-home">
      <PageTitle right={<AddConceptButton />}>Learn</PageTitle>
      <LearnTabs />
      {!course || !progress ? (
        <Empty icon="🎓" action={<AddCourseButton />}>
          Name the patterns you already use. This adds the 22 classic design patterns, each with short cards
          that teach it, questions on it, and a link out to read more.
        </Empty>
      ) : (
        <>
          <div className="mt-2">
            <Button
              variant="primary"
              big
              className="w-full min-h-14 text-[17px]"
              disabled={busy || seeding}
              onClick={() => void start()}
              data-testid="learn-start"
            >
              {doneToday ? 'Start a deck' : "Today's deck"}
            </Button>
            <Button
              big
              className="w-full min-h-14 text-[17px] mt-2.5"
              disabled={seeding || !exercises || !exercises.some((x) => !x.deleted_at)}
              onClick={() => setTesting(true)}
              data-testid="learn-test"
            >
              Test me
            </Button>
            <TestSetupSheet open={testing} onClose={() => setTesting(false)} />
            {doneToday && (
              <p className="mt-2 text-center text-sm text-muted" data-testid="learn-today-done">
                Today&rsquo;s deck is done.
              </p>
            )}
            <div className="mt-3 flex justify-center gap-2" role="group" aria-label="How long">
              <Button
                variant={minutes === ONE_DECK_MINUTES ? 'chipOn' : 'chip'}
                onClick={() => setMinutes(ONE_DECK_MINUTES)}
                data-testid="learn-chip-one"
              >
                One deck, about 3 minutes
              </Button>
              <Button
                variant={minutes === KEEP_GOING_MINUTES ? 'chipOn' : 'chip'}
                onClick={() => setMinutes(KEEP_GOING_MINUTES)}
                data-testid="learn-chip-more"
              >
                Keep going
              </Button>
            </div>
            {none && (
              <p className="mt-3 text-center text-sm text-muted" data-testid="learn-none">
                That is the lot for now. Come back later.
              </p>
            )}
          </div>
          <ul className="group-list mt-6" data-testid="learn-courses">
            <li>
              <Link
                to="/learn/course/$id"
                params={{ id: course.id }}
                className="block px-3.5 py-3 min-h-14"
                data-testid="learn-course"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-[17px] font-medium truncate">{course.spec.title}</span>
                  <span className="text-xs text-faint shrink-0">
                    {progress.done} of {progress.total} done
                  </span>
                </span>
                <span
                  className="block h-px mt-3 bg-line relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={progress.total}
                  aria-valuenow={progress.done}
                  aria-label="Lessons done"
                >
                  <span
                    className="absolute left-0 -top-px h-[3px] bg-accent"
                    style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                  />
                </span>
              </Link>
            </li>
          </ul>
          {gap && !seeding && (
            <div className="mt-4 flex justify-center">
              <AddTeachingButton gap={gap} />
            </div>
          )}
          <div className="mt-5 flex justify-center gap-1">
            <Link
              to="/learn/progress"
              className="min-h-11 px-4 inline-flex items-center text-sm text-muted"
              data-testid="learn-progress-link"
            >
              Progress
            </Link>
            <Link
              to="/learn/settings"
              className="min-h-11 px-4 inline-flex items-center text-sm text-muted"
              data-testid="learn-settings-link"
            >
              Settings
            </Link>
          </div>
        </>
      )}
    </div>
  )
}
